/**
 * React Query hooks layer.
 *
 * The repository functions in `src/data/*` are pure async functions and
 * stay free of React. This file wraps the most-used repository calls in
 * `useQuery` / `useMutation` so that consumers don't have to spell out
 * the same query keys + cache invalidation rules over and over.
 *
 * Conventions:
 *
 *  - **Query keys** are factory functions exported from `keys` so the
 *    same key gets used in `useQuery` and in `invalidateQueries` calls.
 *    Misspellings become impossible.
 *
 *  - **Mutations** invalidate the relevant lists on success — no
 *    consumer needs to remember to do it.
 *
 *  - **Hook names** mirror the repository function names: `listPatients`
 *    in `patients.ts` becomes `useListPatients` here.
 *
 * Components are still free to call the repository functions directly
 * (e.g. inside an `onClick` for a one-off mutation). The hooks just
 * remove boilerplate for the common path.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  patientsRepo,
  appointmentsRepo,
  messagesRepo,
  invoicesRepo,
  organizationsRepo,
  encountersRepo,
  type Encounter,
  type ListPatientsOptions,
  type ListAppointmentsOptions,
  type PatientInsert,
  type PatientUpdate,
  type AppointmentInsert,
  type AppointmentUpdate,
  type InvoiceUpdate,
} from '@/data';

// ─── Query key factories ────────────────────────────────────────────────
//
// Centralising the keys means a typo cannot break invalidation.
// Calling `keys.patients.list(orgId, opts)` always returns the same
// stable key for the same arguments.

export const keys = {
  patients: {
    all: (orgId: string) => ['patients', orgId] as const,
    list: (orgId: string, opts: ListPatientsOptions = {}) =>
      ['patients', orgId, 'list', opts] as const,
    detail: (orgId: string, patientId: string) =>
      ['patients', orgId, 'detail', patientId] as const,
  },
  appointments: {
    all: (orgId: string) => ['appointments', orgId] as const,
    list: (orgId: string, opts: ListAppointmentsOptions = {}) =>
      ['appointments', orgId, 'list', opts] as const,
    detail: (orgId: string, appointmentId: string) =>
      ['appointments', orgId, 'detail', appointmentId] as const,
  },
  messages: {
    all: (orgId: string) => ['messages', orgId] as const,
    unread: (orgId: string) => ['messages', orgId, 'unread'] as const,
    list: (orgId: string) => ['messages', orgId, 'list'] as const,
  },
  invoices: {
    all: (orgId: string) => ['invoices', orgId] as const,
    list: (orgId: string) => ['invoices', orgId, 'list'] as const,
    detail: (orgId: string, invoiceId: string) =>
      ['invoices', orgId, 'detail', invoiceId] as const,
    items: (invoiceId: string) => ['invoice-items', invoiceId] as const,
  },
  organizations: {
    detail: (orgId: string) => ['organization', orgId] as const,
    branding: (orgId: string) => ['organization', orgId, 'branding'] as const,
  },
  encounters: {
    all: (orgId: string) => ['encounters', orgId] as const,
    forPatient: (orgId: string, patientId: string) =>
      ['encounters', orgId, 'patient', patientId] as const,
    activeForPatient: (orgId: string, patientId: string) =>
      ['encounters', orgId, 'patient', patientId, 'active'] as const,
  },
} as const;

// ─── Patients ───────────────────────────────────────────────────────────

/**
 * List patients for the current organisation. Pass `enabled: false` via
 * the options to skip until you have the org id.
 */
export function useListPatients(
  orgId: string | undefined,
  options: ListPatientsOptions = {},
) {
  return useQuery({
    queryKey: keys.patients.list(orgId ?? '', options),
    enabled: !!orgId,
    queryFn: () => patientsRepo.listPatients(orgId!, options),
    // F-05-04: keep stale data visible while a new search is loading so the
    // list never flickers to empty/spinner during keystroke-debounce refetch.
    placeholderData: keepPreviousData,
  });
}

export function useGetPatient(orgId: string | undefined, patientId: string | undefined) {
  return useQuery({
    queryKey: keys.patients.detail(orgId ?? '', patientId ?? ''),
    enabled: !!orgId && !!patientId,
    queryFn: () => patientsRepo.getPatient(orgId!, patientId!),
  });
}

export function useCreatePatient(orgId: string | undefined, userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PatientInsert) => {
      if (!userId || !orgId) {
        throw new Error('useCreatePatient: userId und orgId werden fuer den Audit-Trail benoetigt');
      }
      return patientsRepo.createPatient(input, { userId, organizationId: orgId });
    },
    onSuccess: () => {
      if (orgId) qc.invalidateQueries({ queryKey: keys.patients.all(orgId) });
    },
  });
}

export function useUpdatePatient(orgId: string | undefined, userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, patch }: { patientId: string; patch: PatientUpdate }) => {
      if (!userId || !orgId) {
        throw new Error('useUpdatePatient: userId und orgId werden fuer den Audit-Trail benoetigt');
      }
      return patientsRepo.updatePatient(patientId, patch, { userId, organizationId: orgId });
    },
    // Optimistic Update (UX-Audit Phase 3): die Patient-Detail-Seite und
    // die Liste zeigen den neuen Wert sofort, bevor der Server bestätigt.
    // Schlägt der Server fehl, rollen wir auf den Snapshot zurück. Am
    // Ende invalidieren wir trotzdem, damit Server-Computed-Felder
    // (updated_at, …) und ggf. RLS-bedingte Filter-Drops konsistent sind.
    onMutate: async (vars) => {
      if (!orgId) return { snapshots: [] as Array<readonly [readonly unknown[], unknown]> };
      // cancel laufende Refetches, damit unser optimistisches Bild nicht überschrieben wird
      await qc.cancelQueries({ queryKey: keys.patients.all(orgId) });
      const snapshots = qc.getQueriesData({ queryKey: keys.patients.all(orgId) });
      qc.setQueriesData(
        { queryKey: keys.patients.all(orgId) },
        (old: unknown) => applyPatientPatch(old, vars.patientId, vars.patch),
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: (_data, _err, vars) => {
      if (!orgId) return;
      qc.invalidateQueries({ queryKey: keys.patients.all(orgId) });
      qc.invalidateQueries({ queryKey: keys.patients.detail(orgId, vars.patientId) });
    },
  });
}

/**
 * Apply a patch to either a single Patient (detail-query) or to a list
 * of Patients. Returns the same reference identity when no row matches
 * so React-Query keeps unaffected subscribers stable.
 */
function applyPatientPatch(
  old: unknown,
  patientId: string,
  patch: PatientUpdate,
): unknown {
  if (Array.isArray(old)) {
    let touched = false;
    const next = old.map((row) => {
      const r = row as { id?: string } | null;
      if (r && r.id === patientId) {
        touched = true;
        return { ...r, ...patch };
      }
      return row;
    });
    return touched ? next : old;
  }
  const single = old as { id?: string } | null;
  if (single && single.id === patientId) {
    return { ...single, ...patch };
  }
  return old;
}

// ─── Appointments ───────────────────────────────────────────────────────

export function useListAppointments(
  orgId: string | undefined,
  options: ListAppointmentsOptions = {},
) {
  return useQuery({
    queryKey: keys.appointments.list(orgId ?? '', options),
    enabled: !!orgId,
    queryFn: () => appointmentsRepo.listAppointments(orgId!, options),
  });
}

export function useCreateAppointment(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AppointmentInsert) => appointmentsRepo.createAppointment(input),
    onSuccess: () => {
      if (orgId) qc.invalidateQueries({ queryKey: keys.appointments.all(orgId) });
    },
  });
}

export function useUpdateAppointment(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      patch,
    }: {
      appointmentId: string;
      patch: AppointmentUpdate;
    }) => appointmentsRepo.updateAppointment(appointmentId, patch),
    // Optimistic Update (UX-Audit Phase 3): Termin-Karten in der
    // Tagesübersicht / im Kalender springen sofort um, statt erst nach
    // dem Server-Roundtrip. Rollback bei Fehler; invalidate am Ende
    // räumt Server-Computed-Felder + automation-bedingte Folgequeries
    // (z. B. tasks-for-patient) auf.
    onMutate: async (vars) => {
      if (!orgId) return { snapshots: [] as Array<readonly [readonly unknown[], unknown]> };
      await qc.cancelQueries({ queryKey: keys.appointments.all(orgId) });
      const snapshots = qc.getQueriesData({ queryKey: keys.appointments.all(orgId) });
      qc.setQueriesData(
        { queryKey: keys.appointments.all(orgId) },
        (old: unknown) => applyAppointmentPatch(old, vars.appointmentId, vars.patch),
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSuccess: async (appointment, variables) => {
      // Workflow-Automation Trigger: wenn Termin auf 'completed' geht,
      // löse die org-eigenen Automation-Regeln für 'appointment_completed'
      // aus. Lazy-import, damit der Hook-Modul-Graph keinen festen
      // Strict-Cycle gegen `data/automations.ts` aufbaut.
      if (
        orgId &&
        variables.patch.status === 'completed' &&
        appointment.patient_id
      ) {
        const { runAutomationsForEvent } = await import('./automations');
        const userId = appointment.created_by ?? null;
        await runAutomationsForEvent(
          'appointment_completed',
          {
            organizationId: orgId,
            patientId: appointment.patient_id,
            occurredAt: new Date(),
          },
          userId ?? '',
        );
        qc.invalidateQueries({ queryKey: ['tasks-for-patient', appointment.patient_id] });
      }
    },
    onSettled: () => {
      if (orgId) qc.invalidateQueries({ queryKey: keys.appointments.all(orgId) });
    },
  });
}

/**
 * Apply a patch to either a single Appointment (detail-query) or to a
 * list of appointments (list-queries). Same shape as `applyPatientPatch`
 * — kept inline rather than generic to keep the two patch paths
 * independent if the row shapes ever diverge.
 */
function applyAppointmentPatch(
  old: unknown,
  appointmentId: string,
  patch: AppointmentUpdate,
): unknown {
  if (Array.isArray(old)) {
    let touched = false;
    const next = old.map((row) => {
      const r = row as { id?: string } | null;
      if (r && r.id === appointmentId) {
        touched = true;
        return { ...r, ...patch };
      }
      return row;
    });
    return touched ? next : old;
  }
  const single = old as { id?: string } | null;
  if (single && single.id === appointmentId) {
    return { ...single, ...patch };
  }
  return old;
}

// ─── Messages ───────────────────────────────────────────────────────────

export function useUnreadMessageCount(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.messages.unread(orgId ?? ''),
    enabled: !!orgId,
    queryFn: () => messagesRepo.countUnreadInbound(orgId!),
    refetchInterval: 20_000,
  });
}

// ─── Invoices ───────────────────────────────────────────────────────────

export function useListInvoices(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.invoices.list(orgId ?? ''),
    enabled: !!orgId,
    queryFn: () => invoicesRepo.listInvoices(orgId!),
  });
}

export function useSetInvoiceStatus(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, status }: { invoiceId: string; status: string }) =>
      invoicesRepo.setInvoiceStatus(invoiceId, status),
    onSuccess: () => {
      if (orgId) qc.invalidateQueries({ queryKey: keys.invoices.all(orgId) });
    },
  });
}

export function useUpdateInvoice(orgId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, patch }: { invoiceId: string; patch: InvoiceUpdate }) =>
      invoicesRepo.updateInvoice(invoiceId, patch),
    onSuccess: () => {
      if (orgId) qc.invalidateQueries({ queryKey: keys.invoices.all(orgId) });
    },
  });
}

// ─── Organizations ──────────────────────────────────────────────────────

/**
 * Lightweight branding read used by Arztbrief / PDF generators.
 * Cached aggressively because logos rarely change mid-session.
 */
export function useOrganizationBranding(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.organizations.branding(orgId ?? ''),
    enabled: !!orgId,
    queryFn: () => organizationsRepo.getOrganizationBranding(orgId!),
    staleTime: 5 * 60 * 1000, // 5 min — branding is near-immutable
  });
}

// ─── Encounters ─────────────────────────────────────────────────────────
//
// The migration path away from the deprecated `patients.admission_*`
// columns (ADR 0013, dual-write enabled by PR #252 + #253). UI
// consumers that today read `patient.admission_status`,
// `patient.admission_date`, etc. should switch to these hooks one
// PR at a time. When every reader is migrated, Phase C drops the
// legacy columns.
//
// `useActiveEncounter` returns the most recent non-`entlassen`
// encounter — the typical "is this patient currently admitted?"
// question. Returns `null` for ambulant patients without an open
// encounter (a fresh ADT-A04 with status='ambulant' creates one,
// but historic ambulant rows in patients without backfill don't
// have one).

export function useEncountersForPatient(
  orgId: string | undefined,
  patientId: string | undefined,
) {
  return useQuery({
    queryKey: keys.encounters.forPatient(orgId ?? '', patientId ?? ''),
    enabled: !!orgId && !!patientId,
    queryFn: () => encountersRepo.listEncountersForPatient(orgId!, patientId!),
  });
}

/**
 * The patient's currently active stay (status != 'entlassen'), or
 * `null` when there is no open encounter. UI consumers that show
 * "currently in station 3B" / "admitted on 2026-04-24" use this.
 *
 * Returns the typed `Encounter | null` — never throws when the
 * patient simply has no active encounter.
 */
export function useActiveEncounter(
  orgId: string | undefined,
  patientId: string | undefined,
) {
  return useQuery<Encounter | null>({
    queryKey: keys.encounters.activeForPatient(orgId ?? '', patientId ?? ''),
    enabled: !!orgId && !!patientId,
    queryFn: () => encountersRepo.getActiveEncounter(orgId!, patientId!),
  });
}
